import type { SupSingleVaultV3, SupWithdrawableToken } from "@/types";
import type { Strategies } from "@/../config/swr.config";
import { useMemo } from "react";

import useTokenBalances from "@/hooks/api/useTokenBalances";
import useApi from "@/hooks/api/useApi";
import { getAddress } from "viem";

/**
 * @package api:go
 * @link https://superform-xyz.postman.co/workspace/Superform~fe57d318-f198-4f97-9c50-0b2a5a78dbac/request/3472327-461a7539-f1fc-44a3-98b2-633fdf19733c?ctx=documentation
 */
export default function useSupportedWithdrawableTokens(vaultList: SupSingleVaultV3[], strategy: Strategies = "on-mount") {
  // Forming query string out of vaultIds
  const { data: balance, isLoading: isBalanceLoading } = useTokenBalances(false, "fully-cached");

  const queryUrl = useMemo(
    () => vaultList.reduce<string[]>((acc, vault) => acc.concat([`vaultIds=${vault.id}`]), []).join("&") + `&balances=${balance?.length || 0}`,
    [vaultList, balance]
  );

  const enabled = useMemo(() => Boolean(queryUrl) && !!balance, [queryUrl, balance]);

  const { data, isLoading, isError, error, mutate } = useApi<SupWithdrawableToken[], SupWithdrawableToken[]>(
    {
      path: `/supported/withdrawable-tokens/?${queryUrl}`,
      api: "v2",
    },
    {
      enabled,
      strategy: strategy,
      select: data => {
        const dataWithVaultTokens = [
          // Adding vault tokens to the list
          ...vaultList.reduce<SupWithdrawableToken[]>(
            (acc, vault) =>
              acc.concat(
                vault.assets.map(asset => ({
                  name: asset.name,
                  symbol: asset.symbol,
                  address: asset.contract_address,
                  chainId: vault.chain.id,
                  decimals: asset.decimals,
                  logoURI: asset.image,
                  isVaultToken: true,
                }))
              ),
            []
          ),
          ...data,
        ];

        // Enrich data with balance
        const results = dataWithVaultTokens.map(token => {
          const relevantBalance = balance?.find(
            balanceToken => getAddress(balanceToken.address, parseInt(`${balanceToken.chainId}`)) === getAddress(token.address, parseInt(`${token.chainId}`))
          );
          return {
            ...token,
            amount: relevantBalance?.amount || undefined,
            amountFixedNumber: relevantBalance?.amountFixedNumber || undefined,
            amountInFiat: relevantBalance?.amountInFiat || undefined,
            price: relevantBalance?.price || token.price,
            searchIndex: [token.name, token.symbol, token.address, token.chainId].join(" ").toLowerCase().replace(/\s+/g, ""),
          } as SupWithdrawableToken;
        });

        // filtering duplicated tokens
        return results.filter((value, index, self) => {
          return self.findIndex(token => getAddress(token.address) === getAddress(value.address) && token.chainId == value.chainId) === index;
        });
      },
    }
  );

  return {
    data,
    error,
    isLoading: isBalanceLoading || isLoading,
    isError,
    mutate,
  };
}
